<div class="ov">

  <a class="ov-back" routerLink="/main/dashboard"><i class="fa fa-angle-left" aria-hidden="true"></i> Projects</a>

  <header class="ov-header">
    <div class="ov-identity">
      @if (project(); as p) {
        <app-project-avatar class="ov-avatar" [name]="p.name" [logoUrl]="p.logo_url" />
        <div class="ov-identity__text">
          <h1 class="ov-title" data-testid="overview-title">{{ p.name }}</h1>
          @if (p.status_page_slug) {
            <a class="ov-status-link" href="/main/status/{{ p.status_page_slug }}" data-testid="overview-status-page">
              /status/{{ p.status_page_slug }} <i class="fa fa-external-link" aria-hidden="true"></i>
            </a>
          }
        </div>
      } @else if (projectState() === 'error') {
        <h1 class="ov-title" data-testid="overview-load-error">Couldn’t load this project</h1>
      } @else {
        <div class="sk sk--avatar"></div>
        <div class="sk sk--title"></div>
      }
    </div>

    <nav class="ov-actions" aria-label="Project settings">
      <a class="pw-btn pw-btn--ghost pw-btn--sm" routerLink="/main/project/{{ projectId }}/settings" data-testid="overview-edit">
        <i class="fa fa-pencil" aria-hidden="true"></i> Edit project
      </a>
      <a class="pw-btn pw-btn--ghost pw-btn--sm" routerLink="/main/project/{{ projectId }}/notifications" data-testid="overview-notifications">
        <i class="fa fa-bell-o" aria-hidden="true"></i> Notifications
      </a>
      <a class="pw-btn pw-btn--ghost pw-btn--sm" routerLink="/main/project/{{ projectId }}/api-keys" data-testid="overview-api-keys">
        <i class="fa fa-key" aria-hidden="true"></i> API keys
      </a>
      <a class="pw-btn pw-btn--ghost pw-btn--sm" routerLink="/main/project/{{ projectId }}/analytics" data-testid="overview-analytics">
        <i class="fa fa-line-chart" aria-hidden="true"></i> Status page views
      </a>
    </nav>
  </header>

  <div class="ov-grid">

    <!-- Monitors -------------------------------------------------------- -->
    <section class="panel" data-testid="overview-monitors">
      <header class="panel__head">
        <h2 class="panel__title"><i class="fa fa-heartbeat" aria-hidden="true"></i> Monitors</h2>
        @if (monitorsState() === 'ready' && monitors().length) {
          @if (downCount()) {
            <span class="pill pill--bad" data-testid="overview-monitors-summary">{{ downCount() }} down</span>
          } @else if (maintenanceCount()) {
            <span class="pill pill--warn" data-testid="overview-monitors-summary">Maintenance</span>
          } @else {
            <span class="pill pill--ok" data-testid="overview-monitors-summary">All up</span>
          }
        }
      </header>

      @switch (monitorsState()) {
        @case ('loading') {
          <div class="sk sk--line"></div><div class="sk sk--line"></div><div class="sk sk--line sk--short"></div>
        }
        @case ('error') {
          <p class="panel__empty">Couldn’t load the monitors.</p>
        }
        @default {
          @if (monitors().length) {
            <ul class="monitors">
              @for (m of monitors(); track m.name) {
                <li class="monitor" data-testid="overview-monitor">
                  <span class="monitor__dot monitor__dot--{{ monitorState(m) }}" [title]="monitorState(m)"></span>
                  <span class="monitor__name">{{ m.name }}</span>
                  <span class="monitor__uptime">
                    {{ m.averageLast90Days !== null ? m.averageLast90Days + '%' : '—' }}
                  </span>
                  <span class="monitor__bars" aria-hidden="true">
                    @for (bar of recentBars(m); track $index) {
                      <i [style.background]="bar.color"></i>
                    }
                  </span>
                </li>
              }
            </ul>
            <p class="panel__note">Uptime over 90 days · bars show the last 30</p>
          } @else {
            <p class="panel__empty">No monitors yet. Add components with a URL to check.</p>
          }
        }
      }

      <a class="panel__more" routerLink="/main/project/{{ projectId }}/settings">Manage monitors <i class="fa fa-angle-right" aria-hidden="true"></i></a>
    </section>

    <!-- Errors ---------------------------------------------------------- -->
    <section class="panel" data-testid="overview-errors">
      <header class="panel__head">
        <h2 class="panel__title"><i class="fa fa-bug" aria-hidden="true"></i> Errors</h2>
      </header>

      @switch (errorsState()) {
        @case ('loading') {
          <div class="sk sk--line"></div><div class="sk sk--line"></div><div class="sk sk--line sk--short"></div>
        }
        @case ('error') {
          <p class="panel__empty">Couldn’t load the errors.</p>
        }
        @default {
          @if (errorStats(); as s) {
            <div class="stats">
              <div class="stat">
                <span class="stat__value" data-testid="overview-errors-24h">{{ s.events_last_24h | number }}</span>
                <span class="stat__label">events (24h)</span>
              </div>
              <div class="stat">
                <span class="stat__value" [class.stat__value--bad]="s.unresolved_issues > 0">{{ s.unresolved_issues | number }}</span>
                <span class="stat__label">unresolved</span>
              </div>
              <div class="stat">
                <span class="stat__value">{{ s.users_affected | number }}</span>
                <span class="stat__label">users affected</span>
              </div>
            </div>
          }
          @if (issues().length) {
            <ul class="entries">
              @for (issue of issues(); track issue.uuid) {
                <li>
                  <a class="entry" routerLink="/main/project/{{ projectId }}/errors/{{ issue.uuid }}" data-testid="overview-issue">
                    <span class="entry__main">
                      <span class="entry__title">{{ issue.title }}</span>
                      <span class="entry__sub">{{ issue.times_seen | number }}× · {{ relativeTime(issue.last_seen) }}</span>
                    </span>
                    <span class="level level--{{ issue.level }}">{{ issue.level }}</span>
                  </a>
                </li>
              }
            </ul>
          } @else {
            <p class="panel__empty">No unresolved issues.</p>
          }
        }
      }

      <a class="panel__more" routerLink="/main/project/{{ projectId }}/errors" data-testid="overview-errors-link">All errors <i class="fa fa-angle-right" aria-hidden="true"></i></a>
    </section>

    <!-- Traces ---------------------------------------------------------- -->
    <section class="panel" data-testid="overview-tracing">
      <header class="panel__head">
        <h2 class="panel__title"><i class="fa fa-random" aria-hidden="true"></i> Traces</h2>
      </header>

      @switch (tracingState()) {
        @case ('loading') {
          <div class="sk sk--line"></div><div class="sk sk--line"></div><div class="sk sk--line sk--short"></div>
        }
        @case ('error') {
          <p class="panel__empty">Couldn’t load the traces.</p>
        }
        @default {
          @if (tracingStats(); as t) {
            <div class="stats">
              <div class="stat">
                <span class="stat__value" data-testid="overview-requests-24h">{{ t.sampled ? '≈ ' : '' }}{{ t.transactions_24h | number }}</span>
                <span class="stat__label">requests (24h)</span>
              </div>
              <div class="stat">
                <span class="stat__value" [class.stat__value--bad]="t.error_rate > 0">{{ t.error_rate * 100 | number:'1.0-2' }}%</span>
                <span class="stat__label">failed</span>
              </div>
              <div class="stat">
                <span class="stat__value">{{ formatDuration(t.p95_ms) }}</span>
                <span class="stat__label">p95</span>
              </div>
            </div>
            @if (t.slowest?.length) {
              <p class="panel__subtitle">Slowest endpoints</p>
              <ul class="entries">
                @for (tx of t.slowest.slice(0, 3); track tx.name) {
                  <li class="entry entry--static" data-testid="overview-slow-endpoint">
                    <span class="entry__main">
                      <span class="entry__title">{{ tx.name }}</span>
                      <span class="entry__sub">{{ tx.count | number }} requests</span>
                    </span>
                    <span class="entry__value">{{ formatDuration(tx.avg_ms) }}</span>
                  </li>
                }
              </ul>
            } @else {
              <p class="panel__empty">No traced requests in the last 24 hours.</p>
            }
          }
        }
      }

      <a class="panel__more" routerLink="/main/project/{{ projectId }}/tracing" data-testid="overview-tracing-link">All traces <i class="fa fa-angle-right" aria-hidden="true"></i></a>
    </section>

  </div>
</div>
